import { useEffect, useMemo, useRef, useState } from 'react';
import {
  loadWaSessions,
  loadWaMessages,
  sendWaReply,
} from '../../lib/whatsappInboxService';
import {
  windowOpen,
  windowLabel,
  inboxStats,
  type WaSession,
  type WaMessage,
} from '../../lib/whatsappInbox';

/**
 * « Inbox WhatsApp » — relais humain dans la fenêtre service 24h.
 *   • la fenêtre s'ouvre quand le CLIENT écrit en premier (conformité Meta) ;
 *   • 1re réponse auto (lien de commande) envoyée par le webhook ;
 *   • ici on prend le relais en TEXTE LIBRE tant que la fenêtre est ouverte ;
 *   • fenêtre fermée → réponse désactivée (template approuvé requis).
 * Aucune prospection sortante : on ne fait que RÉPONDRE aux clients qui écrivent.
 */

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

export default function WhatsAppInboxSection() {
  const [sessions, setSessions] = useState<WaSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeWaId, setActiveWaId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WaMessage[]>([]);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const refreshSessions = async () => {
    const res = await loadWaSessions();
    if (res.ok) { setSessions(res.sessions); setError(null); }
    else setError(res.error ?? 'Chargement impossible.');
    setLoading(false);
  };
  useEffect(() => { refreshSessions(); }, []);

  const openThread = async (waId: string) => {
    setActiveWaId(waId);
    setNotice(null);
    setLoadingMsg(true);
    const res = await loadWaMessages(waId);
    setMessages(res.ok ? res.messages : []);
    setLoadingMsg(false);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const active = useMemo(() => sessions.find((s) => s.wa_id === activeWaId) ?? null, [sessions, activeWaId]);
  const stats = useMemo(() => inboxStats(sessions), [sessions]);
  const canReply = active ? windowOpen(active) : false;

  const send = async () => {
    if (!active || !draft.trim()) return;
    setSending(true);
    const res = await sendWaReply(active.wa_id, draft);
    setSending(false);
    if (res.ok) {
      setDraft('');
      await openThread(active.wa_id);
    } else {
      setNotice(res.message);
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-4">
        <h1 className="text-xl font-bold">Inbox WhatsApp</h1>
        <p className="text-sm opacity-70">
          On répond aux clients qui nous écrivent (fenêtre 24h). Pas de prospection sortante :
          la 1re réponse auto part du webhook, ici tu prends le relais humain en texte libre.
        </p>
      </div>

      {/* KPIs */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase text-slate-500">💬 Conversations</div>
          <div className="mt-1 text-2xl font-black text-slate-900">{stats.totalSessions}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs font-bold uppercase text-emerald-700">🟢 Fenêtres ouvertes</div>
          <div className="mt-1 text-2xl font-black text-emerald-800">{stats.openWindows}</div>
          <div className="text-xs text-emerald-700/80">réponse libre possible</div>
        </div>
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="text-xs font-bold uppercase text-indigo-700">✅ Convertis</div>
          <div className="mt-1 text-2xl font-black text-indigo-800">{stats.converted}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase text-slate-500">📣 Opt-in marketing</div>
          <div className="mt-1 text-2xl font-black text-slate-900">{stats.optedIn}</div>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</div>
      ) : loading ? (
        <div className="py-6 text-center text-sm text-slate-400">Chargement…</div>
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <div className="text-3xl">💬</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">Aucune conversation pour l’instant</div>
          <p className="mx-auto mt-1 max-w-md text-xs text-slate-400">
            Les conversations apparaîtront ici dès qu’un client écrira sur le WhatsApp Big Burger.
            Le webhook enregistre le message et envoie automatiquement la 1re réponse avec le lien de commande.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[300px_1fr]">
          {/* Liste des sessions */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="max-h-[560px] overflow-y-auto">
              {sessions.map((s) => {
                const open = windowOpen(s);
                return (
                  <button
                    key={s.id}
                    onClick={() => openThread(s.wa_id)}
                    className={`flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left hover:bg-slate-50 ${activeWaId === s.wa_id ? 'bg-indigo-50' : ''}`}
                  >
                    <span className={`h-2 w-2 shrink-0 rounded-full ${open ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">{s.contact_name || `+${s.wa_id}`}</span>
                      <span className="block truncate text-[11px] text-slate-400">
                        {s.last_inbound_at ? fmtTime(s.last_inbound_at) : '—'} · fenêtre {windowLabel(s)}
                      </span>
                    </span>
                    {s.converted && <span className="shrink-0 text-[11px]">✅</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Conversation */}
          <div className="flex min-h-[560px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {!active ? (
              <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
                Sélectionne une conversation à gauche.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
                  <div>
                    <div className="text-sm font-bold text-slate-800">{active.contact_name || `+${active.wa_id}`}</div>
                    <div className="text-[11px] text-slate-400">+{active.wa_id}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${canReply ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                    {canReply ? `🟢 fenêtre ${windowLabel(active)}` : '⚪ fenêtre fermée'}
                  </span>
                </div>

                <div className="flex-1 space-y-2 overflow-y-auto bg-slate-50 p-4">
                  {loadingMsg ? (
                    <div className="py-6 text-center text-sm text-slate-400">Chargement…</div>
                  ) : messages.length === 0 ? (
                    <div className="py-6 text-center text-sm text-slate-400">Aucun message.</div>
                  ) : (
                    messages.map((m) => {
                      const out = m.direction === 'out';
                      return (
                        <div key={m.id} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${out ? 'bg-indigo-600 text-white' : 'bg-white text-slate-800 border border-slate-200'}`}>
                            <div className="whitespace-pre-wrap break-words">{m.body}</div>
                            <div className={`mt-1 text-[10px] ${out ? 'text-indigo-200' : 'text-slate-400'}`}>
                              {fmtTime(m.created_at)}{out && m.status ? ` · ${m.status}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={endRef} />
                </div>

                <div className="border-t border-slate-200 p-3">
                  {notice && <div className="mb-2 rounded-lg bg-rose-50 px-3 py-1.5 text-xs text-rose-700">{notice}</div>}
                  {canReply ? (
                    <div className="flex items-end gap-2">
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        rows={2}
                        placeholder="Répondre au client…"
                        className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
                      />
                      <button
                        onClick={send}
                        disabled={sending || !draft.trim()}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {sending ? '…' : 'Envoyer'}
                      </button>
                    </div>
                  ) : (
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      La fenêtre 24h est fermée. Le client doit réécrire pour rouvrir la conversation,
                      ou il faut passer par un template WhatsApp approuvé.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
